<script setup>
import { ref } from 'vue'
import { Dialog } from 'quasar'
import { getI18n } from '@/plugins/i18n'

import { readFileAsync } from '@/composables/file-upload'
import { useNotify } from '@/composables/notify'
import FileItem from '@/components/shared/FileItem.vue'
import UrlItem from '@/components/shared/UrlItem.vue'
import { useReadonlyField } from '@/composables/readonly-form'

const props = defineProps({
  modelValue: {
    type: Array,
    required: false,
  },
  urls: {
    type: Array,
    required: false,
  },
  fieldName: {
    type: String,
    required: false,
  },
  cellInfo: {
    type: Object,
    required: false,
  },
  caption: {
    type: Boolean,
    default: false,
  },
  bordered: {
    type: Boolean,
    default: false,
  },
  mobile: {
    type: Boolean,
    default: false,
  },
  single: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  imagesOnly: {
    type: Boolean,
    default: false,
  },
})

const isReadonly = useReadonlyField(() => props.readonly)

const i18n = getI18n()
const { notifyWarning } = useNotify()

const emit = defineEmits(['update:modelValue', 'update:urls', 'download'])

const filesList = ref(props.modelValue || props.cellInfo?.value || [])
const urlsList = ref(props.urls || [])
const attachmentMaxSize = window.config.ATTACHMENT_MAX_SIZE
const attachmentAllowedTypes = props.imagesOnly ? 'image/*' : window.config.ATTACHMENT_ALLOWED_TYPES

const fileInputRef = ref(null)

const isAllowedType = (file) =>
  attachmentAllowedTypes.split(',').some(token => {
    token = token.trim()
    if (token.startsWith('.')) return file.name.toLowerCase().endsWith(token.toLowerCase())
    if (token.endsWith('/*')) return file.type.startsWith(token.slice(0, -1))
    return file.type === token
  })

const onFileInputChange = (e) => {
  Array.from(e.target.files).forEach(file => {
    if (file.size > attachmentMaxSize) {
      const maxMB = Math.round(attachmentMaxSize / (1024 * 1024))
      notifyWarning(i18n.t('warning.fileTooLarge', { name: file.name, max: maxMB }))
      return
    }
    if (!isAllowedType(file)) {
      notifyWarning(i18n.t('warning.fileTypeNotAllowed', { name: file.name }))
      return
    }
    if (filesList.value.find(item => item.name === file.name)) return
    readFileAsync(file).then((uploaded) => {
      filesList.value.push(uploaded)
      emit('update:modelValue', filesList.value)
      props.cellInfo?.setValue(filesList.value)
    })
  })
  e.target.value = ''
}

const removeFile = (file) => {
  filesList.value = filesList.value.filter(f => f.name !== file.name)
  emit('update:modelValue', filesList.value)
  props.cellInfo?.setValue(filesList.value)
}

const openUrlDialog = () => {
  Dialog.create({
    title: i18n.t('common.addUrl'),
    prompt: { model: '', placeholder: 'https://', type: 'text' },
    cancel: true,
    persistent: true,
  }).onOk((input) => {
    let url = input.trim()
    if (!url) return
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`
    if (urlsList.value.find(item => item.url === url)) return
    urlsList.value.push({ url })
    emit('update:urls', urlsList.value)
  })
}

const removeUrl = (urlItem) => {
  urlsList.value = urlsList.value.filter(u => u.url !== urlItem.url)
  emit('update:urls', urlsList.value)
}
</script>

<template>
  <div :class="{'bordered': props.bordered}">
    <span
      v-if="props.caption"
      class="q-text-caption text-grey-8"
      style="font-size: .75rem;"
    >{{ i18n.t('common.attachment', 2) }}</span>

    <div v-if="!isReadonly" class="row q-gutter-x-md q-gutter-y-sm q-mt-xs">
      <q-btn
        v-if="!props.single || (filesList.length === 0 && urlsList.length === 0)"
        icon="mdi-paperclip-plus"
        :label="i18n.t('common.file')"
        @click="fileInputRef.click()"
      />
      <q-btn
        v-if="!props.single || (filesList.length === 0 && urlsList.length === 0)"
        icon="mdi-link-plus"
        :label="i18n.t('common.url')"
        @click="openUrlDialog"
      />
    </div>

    <input
      ref="fileInputRef"
      type="file"
      multiple
      :accept="attachmentAllowedTypes"
      style="display: none"
      @change="onFileInputChange"
    >

    <q-list
      v-if="filesList.length > 0 || urlsList.length > 0"
      bordered
      separator
      :dense="props.mobile"
      class="q-mt-md"
      style="border-radius: .25rem; border-color: #cfcfcf;"
    >
      <FileItem
        v-for="item in filesList"
        :key="item.name"
        :item="item"
        :hide-delete="isReadonly"
        @remove="removeFile"
      />
      <UrlItem
        v-for="urlItem in urlsList"
        :key="urlItem.url"
        :item="urlItem"
        :hide-delete="isReadonly"
        @remove="removeUrl"
      />
    </q-list>
  </div>
</template>

<style scoped>
.bordered {
  border: 1px solid #cfcfcf;
  border-radius: 5px;
  padding: 10px;
}
.url-text {
  word-break: break-all;
  font-size: 0.85rem;
}
</style>
